import { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';

import { localize } from '../../i18n/script';
import { reviewApi, type PublicReview, type ReviewActivityRef } from '../../services/reviewApi';
import Card from '../ui/Card';

import ReviewItem from './ReviewItem';
import ReviewsModalShell from './ReviewsModalShell';

const PAGE_SIZE = 20;
const PREVIEW_COUNT = 3;

interface Props {
  hostId: string;
}

interface ActivityGroup {
  activity: ReviewActivityRef;
  reviews: PublicReview[];
}

// Group a flat, ordered review list by activity, preserving first-seen order.
function groupByActivity(items: PublicReview[]): ActivityGroup[] {
  const groups: ActivityGroup[] = [];
  const index = new Map<string, ActivityGroup>();
  for (const r of items) {
    if (!r.activity) continue;
    let g = index.get(r.activity.id);
    if (!g) {
      g = { activity: r.activity, reviews: [] };
      index.set(r.activity.id, g);
      groups.push(g);
    }
    g.reviews.push(r);
  }
  return groups;
}

function GroupedReviews({ groups, language }: { groups: ActivityGroup[]; language: string }) {
  return (
    <div className="space-y-6">
      {groups.map((g) => (
        <div key={g.activity.id}>
          <Link
            to={`/activity/${g.activity.slug}`}
            className="text-base font-semibold text-gray-900 hover:text-primary"
          >
            {localize(g.activity.titleCN, g.activity.titleEN, language)}
          </Link>
          <ul className="mt-3 space-y-4">
            {g.reviews.map((r) => (
              <ReviewItem key={r.id} review={r} language={language} />
            ))}
          </ul>
        </div>
      ))}
    </div>
  );
}

export default function HostReviews({ hostId }: Props) {
  const { t, i18n } = useTranslation('review');
  const [items, setItems] = useState<PublicReview[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const [showAll, setShowAll] = useState(false);

  const reqRef = useRef(0);
  const load = useCallback(
    async (nextPage: number) => {
      const reqId = ++reqRef.current;
      setLoading(true);
      try {
        const data = await reviewApi.listForHost(hostId, nextPage, PAGE_SIZE);
        if (reqId !== reqRef.current) return;
        setTotal(data.total);
        setPage(data.page);
        setItems((prev) => (nextPage === 1 ? data.items : [...prev, ...data.items]));
      } catch {
        // Non-blocking; leave existing items.
      } finally {
        if (reqId === reqRef.current) setLoading(false);
      }
    },
    [hostId]
  );

  useEffect(() => {
    load(1);
  }, [load]);

  // Render nothing until we know there is at least one review (keeps host page clean).
  if (!loading && total === 0) return null;

  const previewGroups = groupByActivity(items.slice(0, PREVIEW_COUNT));
  const allGroups = groupByActivity(items);

  return (
    <Card padding="lg" className="mt-8">
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-lg sm:text-xl font-bold text-gray-900">{t('title')}</h2>
        {total > 0 && (
          <span className="text-sm text-gray-500">{t('reviewCount', { count: total })}</span>
        )}
      </div>

      <GroupedReviews groups={previewGroups} language={i18n.language} />

      {total > PREVIEW_COUNT && (
        <button
          onClick={() => setShowAll(true)}
          className="mt-5 text-sm text-primary hover:underline font-medium"
        >
          {t('showAll', { count: total })}
        </button>
      )}

      {showAll && (
        <ReviewsModalShell total={total} onClose={() => setShowAll(false)}>
          <GroupedReviews groups={allGroups} language={i18n.language} />
          {items.length < total && (
            <button
              onClick={() => load(page + 1)}
              disabled={loading}
              className="mt-5 text-sm text-primary hover:underline font-medium disabled:opacity-50"
            >
              {t('loadMore')}
            </button>
          )}
        </ReviewsModalShell>
      )}
    </Card>
  );
}
